import React from "react";
import {
	Button,
	CircularProgress,
	Grid2,
	TextField,
	Tooltip,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";

const SnapshotCard = ({
	snapshot,
	index,
	assignment,
	isSubmitting,
	addedSnapshots,
	editedDescriptions,
	editedCustomDescriptions,
	editedHeights,
	editedWidths,
	snapshotNeedsAnalysis,
	onImageClick,
	onGeneration,
	onSaveDescription,
	onSnapshotDeletion,
	onCustomDescriptionChange,
	onHeightChange,
	onWidthChange,
	onDescriptionChange,
}) => {
	const key = `${assignment.assignmentName}-${index}`;
	const isReadOnly =
		assignment.status === "Completed" || assignment.status === "Approved";
	const isAdded = addedSnapshots[assignment.assignmentName]?.includes(
		snapshot
	);
	const hasEdits =
		editedDescriptions.hasOwnProperty(key) ||
		editedCustomDescriptions.hasOwnProperty(key) ||
		editedHeights.hasOwnProperty(key) ||
		editedWidths.hasOwnProperty(key);

	const imageHeight =
		(addedSnapshots[assignment.assignmentName]?.some(
			(element) => element === snapshot
		) &&
			editedDescriptions[key]) ||
		!addedSnapshots[assignment.assignmentName]?.includes(snapshot)
			? "220px"
			: "265px";

	return (
		<Paper
			elevation={1}
			sx={{
				p: { xs: 1, md: 2 },
				mb: 2,
				width: "auto",
				boxSizing: "border-box",
				overflow: "hidden",
			}}
		>
			<Grid2 container spacing={{ xs: 1, md: 3 }} alignItems="flex-start">
				{/* Image Section */}
				<Grid2
					item
					size={{ xs: 12, sm: 4, md: 3 }}
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "flex-start",
					}}
				>
					<Stack>
						<img
							src={snapshot.imageData}
							alt={`Snapshot ${index + 1}`}
							style={{
								width: "100%",
								height: imageHeight,
								objectFit: "contain",
								borderRadius: "5px",
								cursor: "pointer",
							}}
							onClick={() => onImageClick(snapshot.imageData)}
						/>
						<Stack spacing={1} mt={1}>
							{snapshotNeedsAnalysis(snapshot) && (
								<Tooltip title="Use AI to analyze this image and fill in missing details">
									<Button
										size="small"
										variant="outlined"
										color="info"
										onClick={() =>
											onGeneration(
												assignment,
												index,
												snapshot.imageData
											)
										}
										disabled={isSubmitting}
										startIcon={
											isSubmitting ? (
												<CircularProgress size={16} />
											) : null
										}
										fullWidth
									>
										{isSubmitting
											? "Analyzing..."
											: "Analyze Image"}
									</Button>
								</Tooltip>
							)}
							{isAdded
								? hasEdits && (
										<Button
											size="small"
											variant="contained"
											color="primary"
											onClick={() =>
												onSaveDescription(
													assignment.assignmentName,
													index,
													snapshot
												)
											}
											sx={{ mt: 1 }}
										>
											Save All Changes
										</Button>
								  )
								: !snapshot.isSaved && (
										<Button
											size="small"
											variant="outlined"
											color="error"
											onClick={() =>
												onSnapshotDeletion(
													assignment.assignmentName,
													index
												)
											}
										>
											Discard Snapshot
										</Button>
								  )}
						</Stack>
					</Stack>
				</Grid2>

				{/* Details Section */}
				<Grid2 item size={{ xs: 12, sm: 8, md: 9 }}>
					<Stack spacing={1}>
						{/* First row with three text fields */}
						<Grid2 container spacing={{ xs: 1, md: 2 }}>
							<Grid2 item size={{ xs: 12, sm: 4 }}>
								<TextField
									fullWidth
									label="Object Detected"
									size="small"
									variant="outlined"
									value={
										editedCustomDescriptions?.[key] !==
										undefined
											? editedCustomDescriptions[key]
											: snapshot.customDescription || ""
									}
									onChange={(e) =>
										onCustomDescriptionChange(
											assignment.assignmentName,
											index,
											e.target.value
										)
									}
									slotProps={{
										input: { readOnly: isReadOnly },
									}}
								/>
							</Grid2>
							<Grid2 item size={{ xs: 6, sm: 4 }}>
								<TextField
									fullWidth
									label="Height (feet)"
									size="small"
									variant="outlined"
									type="number"
									value={
										editedHeights?.[key] !== undefined
											? editedHeights[key]
											: snapshot.height > 0
											? snapshot.height
											: ""
									}
									onChange={(e) =>
										onHeightChange(
											assignment.assignmentName,
											index,
											e.target.value
										)
									}
									slotProps={{
										input: { readOnly: isReadOnly },
									}}
								/>
							</Grid2>
							<Grid2 item size={{ xs: 6, sm: 4 }}>
								<TextField
									fullWidth
									label="Width (feet)"
									size="small"
									variant="outlined"
									type="number"
									value={
										editedWidths?.[key] !== undefined
											? editedWidths[key]
											: snapshot.width > 0
											? snapshot.width
											: ""
									}
									onChange={(e) =>
										onWidthChange(
											assignment.assignmentName,
											index,
											e.target.value
										)
									}
									slotProps={{
										input: { readOnly: isReadOnly },
									}}
								/>
							</Grid2>
						</Grid2>

						{/* Description Text Field */}
						<TextField
							fullWidth
							label="Description"
							variant="outlined"
							multiline
							rows={4}
							value={
								editedDescriptions[key] !== undefined
									? editedDescriptions[key]
									: snapshot.description
							}
							onChange={(e) =>
								onDescriptionChange(
									assignment.assignmentName,
									index,
									e.target.value
								)
							}
							slotProps={{
								input: { readOnly: isReadOnly },
							}}
						/>
					</Stack>
				</Grid2>
			</Grid2>
		</Paper>
	);
};

export default SnapshotCard;
